import { useEffect, useRef, useState } from 'react'
import { useAiChat, type ChatMessage, type MemoryItem } from '../../hooks/useAiChat'
import { useToast } from '../../hooks/useToast'
import type { PanelSnapshot } from '../../lib/panelSnapshot'

/**
 * Chat IA do BX — drawer lateral recolhível na aba Miguel. Pergunte sobre o
 * painel e ensine regras (memória) que a IA passa a respeitar. Cada resposta
 * pode receber 👍/👎 (feedback que calibra respostas futuras).
 */
export default function MiguelChatDrawer({ panel }: { panel: PanelSnapshot }) {
  const { notify } = useToast()
  const { sending, send, loadHistory, clearHistory, saveFeedback, listMemory, addMemory, deleteMemory } = useAiChat()

  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<'chat' | 'memoria'>('chat')
  const [loaded, setLoaded] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [memory, setMemory] = useState<MemoryItem[]>([])
  const [memInput, setMemInput] = useState('')
  const [feedbackGiven, setFeedbackGiven] = useState<Record<string, 'up' | 'down'>>({})
  const scrollRef = useRef<HTMLDivElement>(null)

  // Carrega histórico + memória na primeira abertura.
  useEffect(() => {
    if (!open || loaded) return
    setLoaded(true)
    loadHistory().then(setMessages)
    listMemory().then(setMemory)
  }, [open, loaded, loadHistory, listMemory])

  // Rola para a última mensagem.
  useEffect(() => {
    if (open && tab === 'chat' && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages, open, tab, sending])

  async function handleSend() {
    const text = input.trim()
    if (!text || sending) return
    const next: ChatMessage[] = [...messages, { role: 'user', content: text }]
    setMessages(next)
    setInput('')
    const res = await send(next, panel)
    if (!res.ok) {
      notify('error', res.error ?? 'Falha ao falar com a IA.')
      return
    }
    setMessages(m => [...m, { id: res.message_id ?? undefined, role: 'assistant', content: res.text ?? '' }])
    // O modelo pode ter salvo uma regra via save_memory — recarrega a memória.
    listMemory().then(setMemory)
  }

  async function handleClear() {
    if (messages.length === 0) return
    if (!confirm('Limpar toda a conversa? As regras da aba Memória não são afetadas.')) return
    const ok = await clearHistory()
    if (!ok) { notify('error', 'Não foi possível limpar a conversa.'); return }
    setMessages([])
    setFeedbackGiven({})
  }

  async function handleFeedback(messageId: string | undefined, rating: 'up' | 'down') {
    if (!messageId || feedbackGiven[messageId]) return
    setFeedbackGiven(f => ({ ...f, [messageId]: rating }))
    const ok = await saveFeedback(messageId, rating)
    if (!ok) notify('error', 'Não foi possível salvar o feedback.')
  }

  async function handleAddMemory() {
    const text = memInput.trim()
    if (!text) return
    const ok = await addMemory(text)
    if (!ok) { notify('error', 'Não foi possível salvar a regra.'); return }
    setMemInput('')
    setMemory(await listMemory())
  }

  async function handleDeleteMemory(id: string) {
    const ok = await deleteMemory(id)
    if (!ok) { notify('error', 'Não foi possível remover a regra.'); return }
    setMemory(m => m.filter(x => x.id !== id))
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-40 flex items-center gap-2 px-4 py-3 rounded-full bg-brand-600 hover:bg-brand-700 text-white shadow-lg shadow-black/20 font-medium text-sm"
        aria-label="Abrir chat com a IA do BX"
      >
        <span aria-hidden className="text-base">💬</span>
        <span className="hidden sm:inline">Pergunte à IA</span>
      </button>
    )
  }

  return (
    <div suppressHydrationWarning>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setOpen(false)} aria-hidden />

      {/* Drawer */}
      <aside className="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-surface border-l border-line flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-line">
          <div className="flex items-center gap-2">
            <span aria-hidden>💬</span>
            <h2 className="text-sm font-semibold text-ink">Assistente do BX</h2>
          </div>
          <div className="flex items-center gap-1">
            {tab === 'chat' && messages.length > 0 && (
              <button
                onClick={handleClear}
                className="text-xs px-2 py-1 rounded-lg text-ink-muted hover:bg-sunken hover:text-ink"
                title="Apagar a conversa (a Memória não é afetada)"
              >
                Limpar
              </button>
            )}
            <button
              onClick={() => setOpen(false)}
              aria-label="Fechar"
              className="text-ink-muted hover:text-ink text-xl leading-none w-8 h-8 flex items-center justify-center rounded-lg hover:bg-sunken"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Abas */}
        <div className="flex gap-1 px-3 py-2 border-b border-line">
          {(['chat', 'memoria'] as const).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                tab === t ? 'bg-brand-600/15 text-brand-700 dark:text-brand-300 border border-brand-600/40' : 'bg-sunken text-ink-muted border border-line'
              }`}
            >
              {t === 'chat' ? 'Conversa' : `Memória${memory.length ? ` · ${memory.length}` : ''}`}
            </button>
          ))}
        </div>

        {tab === 'chat' ? (
          <>
            {/* Mensagens */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              {messages.length === 0 && !sending && (
                <div className="text-center text-ink-faint text-sm py-10">
                  <p className="text-2xl mb-2">🤖</p>
                  <p>Pergunte sobre o estoque, ruptura, fornecedores ou pedidos.</p>
                  <p className="mt-2 text-xs">Dica: ensine regras — ex.: <em>“lembre que encalhe pra mim é acima de 90 dias”.</em></p>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={m.id ?? i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                  <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'bg-brand-600 text-white rounded-br-sm'
                      : 'bg-sunken text-ink border border-line rounded-bl-sm'
                  }`}>
                    {m.content}
                    {m.role === 'assistant' && m.id && (
                      <div className="flex items-center gap-1 mt-2 pt-2 border-t border-line/60">
                        <button
                          onClick={() => handleFeedback(m.id, 'up')}
                          disabled={!!feedbackGiven[m.id]}
                          aria-label="Resposta útil"
                          className={`text-xs px-1.5 py-0.5 rounded ${feedbackGiven[m.id] === 'up' ? 'opacity-100' : 'opacity-60 hover:opacity-100'}`}
                        >👍</button>
                        <button
                          onClick={() => handleFeedback(m.id, 'down')}
                          disabled={!!feedbackGiven[m.id]}
                          aria-label="Resposta ruim"
                          className={`text-xs px-1.5 py-0.5 rounded ${feedbackGiven[m.id] === 'down' ? 'opacity-100' : 'opacity-60 hover:opacity-100'}`}
                        >👎</button>
                        {feedbackGiven[m.id] && <span className="text-[10px] text-ink-faint ml-1">obrigado!</span>}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-sunken border border-line rounded-2xl rounded-bl-sm px-3.5 py-2.5">
                    <div className="flex gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-ink-faint animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-ink-faint animate-bounce" style={{ animationDelay: '120ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-ink-faint animate-bounce" style={{ animationDelay: '240ms' }} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-line p-3">
              <div className="flex items-end gap-2">
                <textarea
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() }
                  }}
                  rows={1}
                  placeholder="Pergunte algo sobre o painel…"
                  className="flex-1 resize-none max-h-32 bg-sunken border border-line rounded-xl px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-brand-600/40"
                />
                <button
                  onClick={handleSend}
                  disabled={sending || !input.trim()}
                  className="px-3.5 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm font-medium"
                >
                  Enviar
                </button>
              </div>
              <p className="text-[10px] text-ink-faint mt-1.5">A IA usa só os dados agregados do seu painel. Confira números importantes antes de decidir.</p>
            </div>
          </>
        ) : (
          /* Aba Memória */
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            <p className="text-xs text-ink-muted">
              Regras que a IA respeita em toda resposta. Ensine pelo chat (“lembre que…”) ou adicione aqui.
            </p>
            <div className="flex items-end gap-2">
              <textarea
                value={memInput}
                onChange={e => setMemInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddMemory() } }}
                rows={1}
                placeholder="Ex.: fornecedor Acme só entrega às terças"
                className="flex-1 resize-none max-h-32 bg-sunken border border-line rounded-xl px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-brand-600/40"
              />
              <button
                onClick={handleAddMemory}
                disabled={!memInput.trim()}
                className="px-3 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm font-medium"
              >
                Salvar
              </button>
            </div>
            {memory.length === 0 ? (
              <p className="text-center text-ink-faint text-sm py-8">Nenhuma regra ainda.</p>
            ) : (
              <ul className="space-y-2">
                {memory.map(item => (
                  <li key={item.id} className="flex items-start justify-between gap-2 bg-sunken border border-line rounded-lg px-3 py-2">
                    <span className="text-sm text-ink leading-snug">{item.content}</span>
                    <button
                      onClick={() => handleDeleteMemory(item.id)}
                      aria-label="Remover regra"
                      className="text-ink-faint hover:text-danger text-lg leading-none shrink-0"
                    >×</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </aside>
    </div>
  )
}
